import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader, Bot, User, Minimize2, UserCheck, PhoneCall, Bell } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';

export default function SupportChat({ companyId, userEmail, userId, userName, isOpen: isOpenProp, onClose }) {
  const { colors } = useTheme();
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenInternal;
  const setIsOpen = onClose ? (val) => { if (!val) onClose(); else setIsOpenInternal(true); } : setIsOpenInternal;
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      senderType: 'ai',
      content: "Hi! I'm your WorkVan assistant. I can help you with questions about features, troubleshooting, billing, and more. How can I help you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState('ai'); // 'ai' or 'live'
  const [conversationId, setConversationId] = useState(null);
  const [waitingForAgent, setWaitingForAgent] = useState(false);
  const [agentName, setAgentName] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewAdminChat, setHasNewAdminChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const channelRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
    if (isOpen) {
      setUnreadCount(0);
      setHasNewAdminChat(false);
    }
  }, [isOpen, isMinimized]);

  // Subscribe to real-time messages when in live mode
  useEffect(() => {
    if (!conversationId || chatMode !== 'live') return;

    channelRef.current = supabase
      .channel(`chat-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          const msg = payload.new;
          // Only add if it's from admin (we already added our own messages)
          if (msg.sender_type === 'admin' || msg.sender_type === 'system') {
            setMessages(prev => [...prev, {
              role: 'assistant',
              senderType: msg.sender_type,
              senderName: msg.sender_name,
              content: msg.content
            }]);
            if (!isOpen) {
              setUnreadCount(prev => prev + 1);
            }
            if (msg.sender_type === 'admin' && !agentName) {
              setAgentName(msg.sender_name);
              setWaitingForAgent(false);
            }
          }
        }
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [conversationId, chatMode, isOpen, agentName]);

  // Subscribe to admin-initiated chats
  useEffect(() => {
    if (!userId) return;

    const adminChatChannel = supabase
      .channel(`admin-chat-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_conversations',
          filter: `customer_user_id=eq.${userId}`
        },
        async (payload) => {
          if (payload.new.started_by === 'admin') {
            // Admin initiated a chat with this user
            setConversationId(payload.new.id);
            setChatMode('live');
            setHasNewAdminChat(true);
            setMessages([{
              role: 'assistant',
              senderType: 'system',
              content: 'A support agent has started a conversation with you.'
            }]);

            // Load any existing messages
            const { data: existingMsgs } = await supabaseData
              .from('chat_messages')
              .select('*')
              .eq('conversation_id', payload.new.id)
              .order('created_at', { ascending: true });

            if (existingMsgs?.length) {
              setMessages(existingMsgs.map(m => ({
                role: m.sender_type === 'customer' ? 'user' : 'assistant',
                senderType: m.sender_type,
                senderName: m.sender_name,
                content: m.content
              })));
            }

            // Flash notification
            if (!isOpen) {
              setUnreadCount(1);
            }
            // Auto-open the chat
            setIsOpen(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(adminChatChannel);
    };
  }, [userId, isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', senderType: 'customer', content: userMessage }]);
    setIsLoading(true);

    try {
      if (chatMode === 'live' && conversationId) {
        // Send to live chat
        await supabaseData.from('chat_messages').insert({
          conversation_id: conversationId,
          sender_type: 'customer',
          sender_id: userId,
          sender_name: userName || userEmail,
          content: userMessage
        });
      } else {
        // Send to AI
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMessage,
            conversationHistory: messages.slice(1).map(m => ({
              role: m.role === 'user' ? 'user' : 'assistant',
              content: m.content
            })),
            companyId,
            userEmail
          })
        });

        const data = await response.json();

        if (response.ok) {
          setMessages(prev => [...prev, { role: 'assistant', senderType: 'ai', content: data.message }]);
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            senderType: 'ai',
            content: data.error || "I'm sorry, I encountered an issue. Please try again or click 'Talk to Human' below."
          }]);
        }
      }
    } catch (error) {

      setMessages(prev => [...prev, {
        role: 'assistant',
        senderType: 'ai',
        content: "I'm having trouble connecting. Please try again or click 'Talk to Human' for live support."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const requestLiveAgent = async () => {
    setWaitingForAgent(true);

    try {
      // Create a conversation for live agent
      const { data: conv, error } = await supabaseData
        .from('chat_conversations')
        .insert({
          company_id: companyId,
          customer_user_id: userId,
          customer_email: userEmail,
          customer_name: userName,
          status: 'waiting',
          started_by: 'ai_escalation',
          ai_summary: messages.slice(1).map(m => `${m.role}: ${m.content}`).join('\n').substring(0, 1000)
        })
        .select()
        .single();

      if (error) throw error;

      setConversationId(conv.id);
      setChatMode('live');

      // Add system message
      setMessages(prev => [...prev, {
        role: 'assistant',
        senderType: 'system',
        content: "Your message has been sent to our team. We'll get back to you as soon as we can - usually within a few hours during business days. Feel free to add more details below."
      }]);

      // Insert a system message in the DB
      await supabaseData.from('chat_messages').insert({
        conversation_id: conv.id,
        sender_type: 'system',
        content: 'Customer requested live support.'
      });

    } catch (err) {

      setWaitingForAgent(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        senderType: 'ai',
        content: "Sorry, I couldn't connect you to a live agent. Please try again or email support@workvanapp.com"
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = [
    { label: 'How do I add a team member?', query: 'How do I add a team member?' },
    { label: 'Billing question', query: 'I have a question about my billing' },
    { label: 'Feature help', query: 'Can you help me understand a feature?' }
  ];

  // When closed, render nothing — the sidebar menu button handles opening
  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: isMinimized ? '300px' : '380px',
        height: isMinimized ? 'auto' : '550px',
        background: colors.bgCard,
        borderRadius: '16px',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '1rem 1.25rem',
          background: chatMode === 'live'
            ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)'
            : 'linear-gradient(135deg, #4ecca3 0%, #3db892 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {chatMode === 'live' ? (
            <UserCheck size={24} color="#fff" />
          ) : (
            <Bot size={24} color="#1a1a1a" />
          )}
          <div>
            <div style={{ fontWeight: '600', color: chatMode === 'live' ? '#fff' : '#1a1a1a', fontSize: '1rem' }}>
              {chatMode === 'live'
                ? (agentName ? agentName : 'Live Support')
                : 'WorkVan Support'}
            </div>
            <div style={{ fontSize: '0.75rem', color: chatMode === 'live' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)' }}>
              {chatMode === 'live'
                ? (waitingForAgent ? 'Message sent' : (agentName ? 'Live chat' : 'Message sent'))
                : 'Powered by AI'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              background: 'rgba(0,0,0,0.1)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.4rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
          >
            <Minimize2 size={18} color={chatMode === 'live' ? '#fff' : '#1a1a1a'} />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              background: 'rgba(0,0,0,0.1)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.4rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label="Close chat"
          >
            <X size={18} color={chatMode === 'live' ? '#fff' : '#1a1a1a'} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                {msg.senderType === 'admin' && msg.senderName && (
                  <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginBottom: '0.25rem', marginLeft: '0.5rem' }}>
                    {msg.senderName}
                  </div>
                )}
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '0.75rem 1rem',
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.role === 'user'
                      ? '#4ecca3'
                      : msg.senderType === 'system'
                        ? colors.border
                        : msg.senderType === 'admin'
                          ? '#3498db'
                          : colors.bgInput,
                    color: msg.role === 'user' ? colors.bgCard : colors.text,
                    fontSize: '0.9rem',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '16px 16px 16px 4px',
                    background: colors.bgInput,
                    color: colors.textMuted,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Loader size={16} className="spin" />
                  {chatMode === 'live' ? 'Sending...' : 'Thinking...'}
                </div>
              </div>
            )}

            {waitingForAgent && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: colors.border,
                  borderRadius: '20px',
                  color: colors.textMuted,
                  fontSize: '0.8rem'
                }}>
                  <Loader size={14} className="spin" />
                  Sending your message...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (show only at start in AI mode) */}
          {messages.length === 1 && chatMode === 'ai' && (
            <div
              style={{
                padding: '0 1rem 0.5rem 1rem',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}
            >
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInputValue(action.query);
                    setTimeout(() => sendMessage(), 100);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '20px',
                    color: colors.textSecondary,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#4ecca3';
                    e.currentTarget.style.color = '#4ecca3';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.color = colors.textSecondary;
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Request human support button (in AI mode only) */}
          {chatMode === 'ai' && messages.length > 2 && !waitingForAgent && (
            <div style={{ padding: '0 1rem 0.5rem 1rem' }}>
              <button
                onClick={requestLiveAgent}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: 'transparent',
                  border: '1px solid #3498db',
                  borderRadius: '8px',
                  color: '#3498db',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#3498db';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#3498db';
                }}
              >
                <PhoneCall size={16} />
                Leave a Message for Support
              </button>
            </div>
          )}

          {/* Input */}
          <div
            style={{
              padding: '1rem',
              borderTop: `1px solid ${colors.borderLight}`,
              display: 'flex',
              gap: '0.75rem'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={chatMode === 'live' ? "Type your message..." : "Ask me anything..."}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '24px',
                color: colors.text,
                fontSize: '0.9rem',
                outline: 'none'
              }}
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !inputValue.trim()}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: inputValue.trim() && !isLoading
                  ? (chatMode === 'live' ? '#3498db' : '#4ecca3')
                  : colors.border,
                border: 'none',
                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              aria-label="Send message"
            >
              <Send size={18} color={inputValue.trim() && !isLoading ? '#fff' : colors.textMuted} />
            </button>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(240, 90, 40, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 6px 30px rgba(240, 90, 40, 0.6); }
        }
      `}</style>
    </div>
  );
}
